import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { panel } from './panelTheme';

// IL CAMPO NUMERICO CON LO STEPPER — il valore che si scrive, e due frecce per chi non vuole
// scriverlo.
//
// Un campo solo per tutti i numeri del pannello: quello accanto a uno slider, quello di un asse,
// quello da solo. Prima erano tre disegni diversi — un `input[type=number]` largo 90 e centrato
// accanto agli slider, le frecce native altrove — e due numeri sulla stessa colonna non si
// allineavano.
//
// ALLINEATO A DESTRA, e non è un gusto: con la colonna a larghezza fissa la x comune c'è a
// destra, ed è quella che mette in fila le cifre per ordine di grandezza. La colonna delle unità
// sta dentro il campo perché il grado non è un'etichetta della riga, è parte del valore.
//
// LE FRECCE SONO UN ACCELERATORE, non l'unico modo. Misurano 18×12, sotto il minimo di WCAG
// 2.5.8: vale l'eccezione equivalente, perché la stessa cosa si ottiene dal campo — che accetta
// il numero digitato, risponde a Su/Giù e misura 78×26. Per questo hanno `tabindex={-1}` e sono
// nascoste a chi legge con lo schermo. Se un giorno diventassero l'unico modo di cambiare il
// valore, l'eccezione decade e vanno portate a 24×24.
//
// FUORI FUOCO SI TRASCINA, DENTRO FUOCO SI SCRIVE — è la regola che i due cursori dichiarano,
// `ew-resize` e `text`, ed è quella del mockup. Il clic non trascina: dà il fuoco, e da lì il campo
// è un campo. Il trascinamento parte dopo 3px, così una mano ferma resta un clic, e ogni pixel vale
// un passo: la corsa di un valore da 0 a 1 a passo 0.01 sono 100px.

const FIELD_WIDTH = '78px';
const FIELD_HEIGHT = '26px';
const SCRUB_THRESHOLD = 3;

const Field = styled.span<{ $full?: boolean }>`
    display: flex;
    align-items: center;
    flex: ${(props) => (props.$full ? '1 1 auto' : '0 0 auto')};
    width: ${(props) => (props.$full ? 'auto' : FIELD_WIDTH)};
    min-width: 0;
    height: ${FIELD_HEIGHT};
    border: 1px solid ${panel.line};
    border-radius: 5px;
    background: ${panel.field};
    overflow: hidden;

    &:focus-within {
        border-color: ${panel.accent};
    }

    /* SPENTO, IL CAMPO PERDE LA PROPRIA SUPERFICIE. Il fondo incassato è ciò che dice «qui si
       scrive»: un campo che non si può scrivere e se lo tiene continua a invitare. Resta
       l'anello, quindi la riga si legge ancora e si vede dov'è il valore. */
    &:has(input:disabled) {
        background: transparent;
    }
`;

// TRE PIXEL PER LATO E NON CINQUE. La lettera dell'asse è un'etichetta dentro il campo, e in una
// terna ogni pixel che si prende lo toglie al numero — che è la cosa da leggere. Sotto i tre non
// si scende: la lettera si attaccherebbe al bordo e al numero, e smetterebbe di leggersi come una
// colonna a sé.
const Axis = styled.span<{ $axis?: 'x' | 'y' | 'z' }>`
    flex-shrink: 0;
    padding: 0 3px;
    font-size: 9.5px;
    font-weight: 700;
    color: ${(props) => (props.$axis === 'x' ? panel.axisX : props.$axis === 'y' ? panel.axisY : props.$axis === 'z' ? panel.axisZ : panel.inkMuted)};
`;

// `touch-action: pan-y`: su uno schermo a tocco lo scorrimento verticale resta al pannello, e il
// gesto orizzontale arriva al campo.
const ValueInput = styled.input`
    width: 100%;
    min-width: 0;
    padding: 0 3px 0 0;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    text-align: right;
    color: ${panel.ink};
    cursor: ew-resize;
    touch-action: pan-y;

    &:focus {
        outline: none;
        cursor: text;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

const Unit = styled.span`
    flex-shrink: 0;
    padding-right: 2px;
    font-size: 9.5px;
    color: ${panel.inkMuted};
    pointer-events: none;
`;

const Steps = styled.span`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    align-self: stretch;
    width: 16px;
    border-left: 1px solid ${panel.line};

    /* Spento col campo: due frecce vive su un campo morto sono una promessa. */
    ${Field}:has(input:disabled) & {
        opacity: 0.35;
        pointer-events: none;
    }
`;

// La linea fra su e giù sta sulla freccia in alto: una dichiarazione sola invece di un bordo per
// metà.
const Step = styled.button<{ $up?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 0;
    padding: 0;
    border: 0;
    border-bottom: ${(props) => (props.$up ? `1px solid ${panel.line}` : '0')};
    background: transparent;
    color: ${panel.inkMuted};
    cursor: pointer;
    transition:
        background 110ms ease,
        color 110ms ease;

    &:hover {
        background: ${panel.line};
        color: ${panel.ink};
    }

    svg {
        width: 8px;
        height: 8px;
        pointer-events: none;
    }
`;

const ChevronUp = () => (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'>
        <path d='m5 15 7-7 7 7' />
    </svg>
);

const ChevronDown = () => (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'>
        <path d='m5 9 7 7 7-7' />
    </svg>
);

export type PropertiesPanelNumberFieldProps = {
    id?: string;
    value: number;
    min?: number;
    max?: number;
    step?: number;
    /** Il suffisso dentro il campo: «°», «m». Parte del valore, non della riga. */
    unit?: string;
    /** La lettera di un asse, col colore dei gizmo. */
    axis?: 'x' | 'y' | 'z';
    /** La lettera scritta, quando non è l'asse: sulle coordinate di una texture i colori di X e Y si chiamano U e V. */
    letter?: string;
    /** Occupa tutta la larghezza disponibile invece dei 78px della colonna dei valori. */
    full?: boolean;
    ariaLabel?: string;
    onChange?: (value: number) => void;
    disabled?: boolean;
};

export const PropertiesPanelNumberField: React.FC<PropertiesPanelNumberFieldProps> = ({
    id,
    value,
    min,
    max,
    step,
    unit,
    axis,
    letter,
    full,
    ariaLabel,
    onChange,
    disabled
}) => {
    // QUANTE CIFRE SI MOSTRANO LO DICE IL PASSO. Con un passo da un centesimo, «-0,000123» non è
    // più preciso: è solo più lungo, e in una colonna di tre campi non ci sta — si leggeva
    // «-0,0(», cioè non si leggeva affatto. Con lo stesso passo, «0» va scritto «0.00»: due
    // numeri incolonnati si confrontano dalla virgola, e senza gli zeri finali la virgola non
    // sta sotto la virgola.
    //
    // PER QUESTO IL CAMPO È DI TESTO e non `type=number`: un campo numerico tiene un NUMERO, e un
    // numero non sa di avere due decimali — `0.00` ci rientra come zero e si ridisegna «0». Le
    // cifre di troppo le tolgono già il nostro stepper e la validazione qui sotto; quello che si
    // perde è la tastiera numerica del telefono, che `inputMode` restituisce.
    //
    // MENTRE SI SCRIVE COMANDA CHI SCRIVE: finché il campo ha il fuoco si mostra quello che si
    // batte — battendo «0.» un campo riformattato lo rimangerebbe prima della cifra dopo.
    const [draft, setDraft] = useState<string | null>(null);

    // Il trascinamento in corso. Non è stato: non ridisegna niente, il valore arriva da `onChange`
    // come arriva dalle frecce.
    const scrub = useRef<{ x: number; from: number; pointer: number; moving: boolean } | null>(null);

    const decimalsOf = (amount: number) => (String(amount).split('.')[1] ?? '').length;
    const decimals = step === undefined ? undefined : decimalsOf(step);
    const shown = draft ?? (decimals === undefined ? String(value) : value.toFixed(decimals));

    // Frecce, tasti e trascinamento consegnano da qui: dentro gli estremi e alla finezza del passo.
    // Un passo da 0.05 sommato in virgola mobile dà 1.0500000000000003: si arrotonda alla stessa
    // finezza del passo, che è quella con cui il valore è stato dichiarato.
    const deliver = (next: number) => {
        if (min !== undefined && next < min) next = min;
        if (max !== undefined && next > max) next = max;

        const decimals = decimalsOf(step ?? 1);
        onChange?.(decimals ? Number(next.toFixed(decimals)) : next);
    };

    const nudge = (direction: 1 | -1) => deliver(value + direction * (step ?? 1));

    return (
        <Field $full={full}>
            {axis && <Axis $axis={axis}>{letter ?? axis.toUpperCase()}</Axis>}
            <ValueInput
                id={id}
                type='text'
                inputMode='decimal'
                value={shown}
                disabled={disabled}
                aria-label={ariaLabel}
                role='spinbutton'
                aria-valuenow={value}
                aria-valuemin={min}
                aria-valuemax={max}
                onFocus={() => setDraft(shown)}
                onBlur={() => setDraft(null)}
                onPointerDown={(event) => {
                    if (disabled || event.button !== 0 || document.activeElement === event.currentTarget) return;

                    // Niente fuoco e niente cursore di testo finché non si sa se è un clic o un
                    // trascinamento. La cattura subito, e non a trascinamento partito: così il rilascio
                    // arriva qui anche fuori dal campo, e un trascinamento non resta mai a metà.
                    event.preventDefault();
                    event.currentTarget.setPointerCapture(event.pointerId);
                    scrub.current = { x: event.clientX, from: value, pointer: event.pointerId, moving: false };
                }}
                onPointerMove={(event) => {
                    const drag = scrub.current;
                    if (!drag || event.pointerId !== drag.pointer) return;

                    const dx = event.clientX - drag.x;
                    if (!drag.moving && Math.abs(dx) < SCRUB_THRESHOLD) return;

                    drag.moving = true;
                    deliver(drag.from + Math.round(dx) * (step ?? 1));
                }}
                onPointerUp={(event) => {
                    const drag = scrub.current;
                    scrub.current = null;
                    if (drag && !drag.moving) event.currentTarget.focus();
                }}
                onPointerCancel={() => {
                    scrub.current = null;
                }}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        nudge(1);
                    }
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        nudge(-1);
                    }
                    if (event.key === 'Enter') event.currentTarget.blur();
                }}
                onChange={(event) => {
                    setDraft(event.target.value);

                    // Si consegna solo un numero. «-», «0.» e il campo vuoto sono passaggi verso un
                    // numero, non numeri: si lasciano scrivere e non si mandano a nessuno.
                    const parsed = Number(event.target.value.replace(',', '.'));
                    if (event.target.value.trim() !== '' && !isNaN(parsed)) onChange?.(parsed);
                }}
            />
            {unit && <Unit>{unit}</Unit>}
            <Steps aria-hidden='true'>
                <Step type='button' $up tabIndex={-1} disabled={disabled} onClick={() => nudge(1)}>
                    <ChevronUp />
                </Step>
                <Step type='button' tabIndex={-1} disabled={disabled} onClick={() => nudge(-1)}>
                    <ChevronDown />
                </Step>
            </Steps>
        </Field>
    );
};
